"use client";

import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { CalendarClock, ChevronRight, Users } from "lucide-react";
import { Skeleton, cn } from "@chotca/ui";
import {
  useEmployerApplicants,
  useEmployerInterviews,
} from "@chotca/api-client";

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}
function hhmm(isoStr: string): string {
  const d = new Date(isoStr);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
// Desktop (md+) shows the 5-latest lists per card. Mobile always stays compact —
// the dashboard route renders a dedicated "Cần xử lý" feed below the cards once
// the profile is complete, so the cards don't need to expand on phones.
function useIsDesktop(): boolean {
  const [desktop, setDesktop] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const sync = () => setDesktop(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return desktop;
}

/**
 * Dashboard entry-point cards. Two states:
 *  - profile incomplete → four compact number cards.
 *  - profile 100% → same four cards, each expanded to show its 5 latest items.
 */
export function DashboardCards({
  applicantsTotal,
  loading,
}: {
  applicantsTotal: number;
  // Loading flags for the parent-supplied counts (postings/applicants).
  // Per-card list loading is read from this component's own queries below.
  loading?: { postings?: boolean; applicants?: boolean };
}) {
  const t = useTranslations("dashboard");
  const nav = useNavigate();
  const isDesktop = useIsDesktop();
  const showLists = isDesktop;

  const interviews = useEmployerInterviews({
    from: startOfToday().toISOString(),
    to: endOfToday().toISOString(),
  });
  const todayCount = interviews.data?.items.length ?? 0;

  // Latest applicants only matter (and only fetch) in the expanded state.
  const latestApplicants = useEmployerApplicants(
    showLists ? { tab: "new", page: 1, limit: 5 } : {},
  );

  return (
    <div
      className={cn(
        "grid gap-3",
        // Always 2 per row on desktop (4 cards = 2 rows). On mobile the compact
        // number cards go 2-up, while list cards stack 1-up.
        showLists ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2",
      )}
    >
      <Card
        icon={<Users size={16} />}
        tone="brand"
        label={t("statApplicantsToday")}
        value={applicantsTotal}
        valueLoading={loading?.applicants}
        listLoading={latestApplicants.isLoading}
        expanded={showLists}
        actionLabel={t("cardViewDetails")}
        onAction={() => nav("/applicants")}
        onFindCandidates={() => nav("/candidates")}
        emptyText={t("emptyApplicants")}
        rows={(latestApplicants.data?.items ?? []).map((a) => ({
          key: a.application.id,
          main: a.worker.fullName,
          meta: a.worker.currentDistrict ?? undefined,
          to: "/applicants",
        }))}
      />

      <Card
        icon={<CalendarClock size={16} />}
        tone="sun"
        label={t("statInterviewsToday")}
        value={todayCount}
        valueLoading={interviews.isLoading}
        listLoading={interviews.isLoading}
        expanded={showLists}
        actionLabel={t("cardViewDetails")}
        onAction={() => nav("/interviews")}
        emptyText={t("emptyInterviews")}
        rows={(interviews.data?.items ?? []).slice(0, 5).map((it) => ({
          key: it.inviteId,
          main: it.workerFullName ?? it.workerMaskedName,
          lead: hhmm(it.startAt),
          to: "/interviews",
        }))}
      />
    </div>
  );
}

type Row = {
  key: string;
  main: string;
  meta?: string;
  lead?: string;
  // Color the lead green for credit gains (+).
  positive?: boolean;
  // Omit to render a non-clickable row (e.g. credit ledger entries).
  to?: string;
};

function Card({
  icon,
  tone,
  label,
  value,
  valueLoading,
  listLoading,
  expanded,
  rows,
  actionLabel,
  onAction,
  onFindCandidates,
  emptyText,
}: {
  icon: React.ReactNode;
  tone: "lime" | "brand" | "sun" | "mist";
  label: string;
  value: number;
  valueLoading?: boolean;
  listLoading?: boolean;
  expanded: boolean;
  rows: Row[];
  actionLabel: string;
  onAction: () => void;
  onFindCandidates?: () => void;
  emptyText: string;
}) {
  const t = useTranslations("dashboard");
  const iconBg =
    tone === "lime"
      ? "bg-lime text-ink"
      : tone === "brand"
        ? "bg-lime-light text-lime-dark"
        : tone === "sun"
          ? "bg-sun-light text-sun-dark"
          : "bg-mist text-ink";

  const header = (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "grid h-7 w-7 shrink-0 place-items-center rounded-full",
          iconBg,
        )}
      >
        {icon}
      </span>
      <span className="text-xs font-bold text-ink/80">{label}</span>
      {valueLoading ? (
        <Skeleton className="ml-auto h-7 w-10 rounded-md" />
      ) : (
        <span className="ml-auto tabular-nums text-2xl font-extrabold text-ink">
          {value.toLocaleString("vi-VN")}
        </span>
      )}
    </div>
  );

  // Compact (mobile, profile incomplete): number + single inline action.
  if (!expanded) {
    return (
      <div className="flex flex-col rounded-2xl border-[1.5px] border-smoke bg-paper p-4">
        {header}
        <button
          type="button"
          onClick={onAction}
          className="mt-2 self-start text-left text-xs font-semibold text-ink/70 transition hover:text-ink"
        >
          {actionLabel}
          <ChevronRight
            size={14}
            className="ml-0.5 inline-block align-text-bottom"
          />
        </button>
      </div>
    );
  }

  // Expanded (desktop / complete): header + fixed-height list/empty + actions.
  return (
    <div className="flex flex-col rounded-2xl border-[1.5px] border-smoke bg-paper p-4">
      {header}

      <div className="mt-3 h-48 overflow-hidden border-t border-smoke pt-1">
        {listLoading ? (
          <CardListSkeleton />
        ) : rows.length > 0 ? (
          <ul className="divide-y divide-smoke">
            {rows.map((r) => {
              const inner = (
                <>
                  {r.lead ? (
                    <span
                      className={cn(
                        "w-10 shrink-0 font-bold tabular-nums",
                        r.positive ? "text-lime-dark" : "text-ink",
                      )}
                    >
                      {r.lead}
                    </span>
                  ) : null}
                  <span className="truncate font-medium text-ink">
                    {r.main}
                  </span>
                  {r.meta ? (
                    <span className="ml-auto shrink-0 text-xs text-ash">
                      {r.meta}
                    </span>
                  ) : null}
                </>
              );
              return (
                <li key={r.key}>
                  {r.to ? (
                    <Link
                      to={r.to}
                      className="flex items-center gap-2 py-2 text-sm transition hover:opacity-70"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2 py-2 text-sm">
                      {inner}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <EmptyCardGlyph />
            <p className="text-xs text-ash">{emptyText}</p>
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        {onFindCandidates ? (
          <button
            type="button"
            onClick={onFindCandidates}
            className="flex-1 rounded-full bg-lime px-3 py-2 text-xs font-bold text-ink transition hover:brightness-95"
          >
            {t("cardFindCandidates")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onAction}
          className="flex-1 rounded-full border border-smoke px-3 py-2 text-xs font-bold text-ink transition hover:bg-mist"
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

/** Shimmer rows that fill the fixed-height list area while a card loads. */
function CardListSkeleton() {
  return (
    <ul className="divide-y divide-smoke">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-center gap-2 py-2.5">
          <Skeleton
            className="h-3.5 flex-1 rounded-md"
            style={{ "--shimmer-delay": `${i * 80}ms` } as React.CSSProperties}
          />
          <Skeleton className="h-3 w-9 rounded-md" />
        </li>
      ))}
    </ul>
  );
}

/** Compact empty-state illustration sized for a dashboard card's list area. */
function EmptyCardGlyph() {
  return (
    <svg
      width="72"
      height="54"
      viewBox="0 0 72 54"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <ellipse cx="36" cy="34" rx="28" ry="18" fill="#E8E8E4" opacity="0.5" />
      <rect
        x="18"
        y="12"
        width="36"
        height="30"
        rx="7"
        fill="white"
        stroke="#E0E0DA"
        strokeWidth="1.5"
        strokeDasharray="3 3"
      />
      <rect x="24" y="20" width="16" height="3" rx="1.5" fill="#D4D4CE" />
      <rect x="24" y="27" width="24" height="3" rx="1.5" fill="#E8E8E4" />
      <rect x="24" y="34" width="12" height="3" rx="1.5" fill="#E8E8E4" />
      <circle cx="53" cy="14" r="4" fill="#32ffaa" opacity="0.55" />
      <circle cx="60" cy="9" r="2" fill="#32ffaa" opacity="0.3" />
    </svg>
  );
}
